import React from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, spacing } from '../theme/tokens';
import { Button, Card, Icon, BrandMark } from '../components/ui';

// A representative set of brands that have logos in /public/logos.
const LOGO_WALL = [
  'audi', 'bmw', 'mercedes-benz', 'ford', 'honda', 'nissan', 'jeep',
  'porsche', 'ferrari', 'lamborghini', 'jaguar', 'land-rover', 'dodge',
  'mazda', 'hyundai', 'kia', 'lexus', 'mini', 'cadillac', 'maserati', 'fiat',
  'peugeot', 'renault', 'chevrolet', 'subaru', 'suzuki', 'alfa-romeo', 'bentley'
];

const FEATURES = [
  { icon: 'search', title: 'Find Parts Fast', body: 'Search a deep, structured database by make, model, variant and year.' },
  { icon: 'inventory_2', title: 'Manage Inventory', body: 'Contribute and curate parts data with role-based controls.' },
  { icon: 'groups', title: 'Track Clients', body: 'Map, leaderboard and analytics for your whole sales team.' },
  { icon: 'bolt', title: 'Work Anywhere', body: 'A fast, responsive PWA that feels native on phone and desktop.' }
];

const Landing = () => {
  const navigate = useNavigate();

  const Logo = ({ slug }) => (
    <span style={styles.wallItem}>
      <img
        src={`/logos/${slug}.png`}
        alt=""
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={styles.wallImg}
      />
    </span>
  );

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes marqueeL { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes marqueeR { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        @keyframes floatOrb { 0%,100% { transform: translate(0,0); } 50% { transform: translate(20px,-26px); } }
        @keyframes riseIn { from { opacity:0; transform: translateY(24px);} to {opacity:1; transform:translateY(0);} }
      `}</style>

      {/* Decorative drifting brand-logo wall */}
      <div style={styles.wall} aria-hidden="true">
        <div style={{ ...styles.wallRow, animation: 'marqueeL 60s linear infinite' }}>
          {[...LOGO_WALL, ...LOGO_WALL].map((s, i) => <Logo key={`a${i}`} slug={s} />)}
        </div>
        <div style={{ ...styles.wallRow, animation: 'marqueeR 75s linear infinite' }}>
          {[...LOGO_WALL.slice().reverse(), ...LOGO_WALL.slice().reverse()].map((s, i) => <Logo key={`b${i}`} slug={s} />)}
        </div>
      </div>

      {/* Floating green orbs */}
      <div style={{ ...styles.orb, top: '-80px', right: '-60px', animation: 'floatOrb 11s ease-in-out infinite' }} aria-hidden="true" />
      <div style={{ ...styles.orb, bottom: '-120px', left: '-80px', width: 360, height: 360, animation: 'floatOrb 14s ease-in-out infinite reverse' }} aria-hidden="true" />

      <div style={styles.content}>
        <div style={styles.hero}>
          <div style={{ animation: 'riseIn 0.7s ease both' }}>
            <BrandMark size={96} />
          </div>
          <h1 style={styles.title}>PAR Car Parts Manager</h1>
          <p style={styles.tagline}>
            The professional way to find, manage and move car parts. Built for speed,
            made for your whole team.
          </p>
          <div style={styles.ctaRow}>
            <Button size="lg" icon="login" onClick={() => navigate('/login')}>Sign in</Button>
            <Button size="lg" variant="ghost" iconRight="arrow_downward" onClick={() => {
              document.getElementById('landing-features')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              Explore
            </Button>
          </div>
          <div style={styles.stats}>
            <Stat icon="directions_car" value="300+" label="Car brands" />
            <Stat icon="database" value="Live" label="Parts database" />
            <Stat icon="verified" value="Secure" label="Google sign-in" />
          </div>
        </div>

        <div id="landing-features" style={styles.features}>
          {FEATURES.map((f) => (
            <Card key={f.title} title={f.title} titleIcon={f.icon} action={false} hoverable padding="24px">
              {f.body}
            </Card>
          ))}
        </div>

        <div style={styles.footer}>
          <span style={{ color: colors.textFaint, fontSize: 13 }}>
            New users start on the Silver tier. Upgrade for unlimited search and contribution tools.
          </span>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon, value, label }) => (
  <div style={styles.stat}>
    <Icon name={icon} size={26} color={colors.parGreenBright} />
    <div>
      <div style={{ fontWeight: 800, fontSize: 18, color: colors.text }}>{value}</div>
      <div style={{ fontSize: 12, color: colors.textMuted }}>{label}</div>
    </div>
  </div>
);

const styles = {
  page: { position: 'relative', minHeight: '100vh', overflow: 'hidden' },
  wall: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    justifyContent: 'center', gap: 60, opacity: 0.06, pointerEvents: 'none', zIndex: 0
  },
  wallRow: { display: 'flex', gap: 48, width: 'max-content' },
  wallItem: { flexShrink: 0 },
  wallImg: { width: 64, height: 64, objectFit: 'contain', filter: 'grayscale(1) brightness(2)' },
  orb: {
    position: 'absolute', width: 280, height: 280, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(47,182,92,0.35) 0%, rgba(47,182,92,0) 70%)',
    filter: 'blur(20px)', zIndex: 0, pointerEvents: 'none'
  },
  content: {
    position: 'relative', zIndex: 1, maxWidth: 1080, margin: '0 auto',
    padding: '64px 24px 48px', display: 'flex', flexDirection: 'column', gap: spacing.xxl
  },
  hero: { textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 },
  title: { margin: 0, fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 800, color: colors.text, letterSpacing: -0.5 },
  tagline: { margin: 0, maxWidth: 620, fontSize: 'clamp(15px, 2.4vw, 19px)', color: colors.textMuted, lineHeight: 1.55 },
  ctaRow: { display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 8 },
  stats: { display: 'flex', gap: 28, flexWrap: 'wrap', justifyContent: 'center', marginTop: 18 },
  stat: { display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' },
  features: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 },
  footer: { textAlign: 'center', borderTop: `1px solid ${colors.glassBorder}`, paddingTop: 24 }
};

export default Landing;
